'use client';

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { usePermissions } from '@/hooks/usePermissions';
import { PageState } from '@/components/common/feedback/PageState';
import {
  AlertTriangle,
  RefreshCw,
  AlertCircle,
  Copy,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  PieChart,
  Search,
  X,
} from 'lucide-react';
import {
  queryErrorLogs,
  ErrorLogItem,
  QueryErrorLogParams,
  formatDuration,
  getHttpMethodColor,
} from '@/services/api/logs';
import { Pagination } from '../_components/Pagination';

/**
 * 错误日志页面
 * 查看和分析系统错误
 */
export default function ErrorLogsPage() {
  const { t } = useTranslation();
  const { hasPermission, isAdmin } = usePermissions();
  
  const [isLoading, setIsLoading] = useState(false);
  const [logs, setLogs] = useState<ErrorLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [error, setError] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [summary, setSummary] = useState<{
    total: number;
    byType: Record<string, number>;
    byHour: { hour: string; count: number }[];
  } | null>(null);

  // 筛选条件
  const [filters, setFilters] = useState<QueryErrorLogParams>({
    startTime: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    endTime: new Date().toISOString(),
  });
  const [keyword, setKeyword] = useState('');
  const [timeRange, setTimeRange] = useState('24h');

  const canAccess = isAdmin || hasPermission('log:read');

  // 时间范围选项
  const timeRangeOptions = [
    { value: '1h', label: t.logs.common.timeRange.last1h },
    { value: '6h', label: t.logs.common.timeRange.last6h },
    { value: '24h', label: t.logs.common.timeRange.last24h },
    { value: '7d', label: t.logs.common.timeRange.last7d },
  ];

  // 更新时间范围
  const handleTimeRangeChange = (value: string) => {
    setTimeRange(value);
    const now = new Date();
    let startTime: Date;
    
    switch (value) {
      case '1h':
        startTime = new Date(now.getTime() - 60 * 60 * 1000);
        break;
      case '6h':
        startTime = new Date(now.getTime() - 6 * 60 * 60 * 1000);
        break;
      case '24h':
        startTime = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case '7d':
        startTime = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      default:
        return;
    }
    
    setFilters(prev => ({
      ...prev,
      startTime: startTime.toISOString(),
      endTime: now.toISOString(),
    }));
  };

  // 加载错误日志
  const loadLogs = useCallback(async () => {
    if (!canAccess) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const params: QueryErrorLogParams = {
        ...filters,
        keyword: keyword || undefined,
        page,
        limit,
      };
      
      const response = await queryErrorLogs(params);
      setLogs(response.items || []);
      setTotal(response.total || 0);
      setSummary(response.summary || null);
    } catch (err) {
      console.error('Failed to load error logs:', err);
      setError(t.logs.error.queryFailed);
    } finally {
      setIsLoading(false);
    }
  }, [canAccess, filters, keyword, page, limit, t]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // 复制到剪贴板
  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // 无权限提示
  if (!canAccess) {
    return (
      <div className="p-6 flex items-center justify-center">
        <PageState
          variant="error"
          title={t.common.permissionDenied}
          description={t.common.permissionDeniedDetail}
          layout="center"
          className="mx-auto w-full max-w-2xl border-0 bg-transparent"
        />
      </div>
    );
  }

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="h-full flex flex-col bg-[#f7f8fa]">
      {/* 主内容区域 - 可滚动 */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-[1400px] mx-auto space-y-4">
          {/* 错误摘要 */}
          {summary && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* 按类型分布 */}
              <div className="bg-white rounded-lg border border-[#e5e6eb] p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <PieChart className="w-4 h-4 text-[#f54a45]" />
                  <h3 className="text-sm font-medium text-[#1f2329]">
                    {t.logs.errors.byType}
                  </h3>
                </div>
                {Object.keys(summary.byType).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(summary.byType)
                      .sort(([, a], [, b]) => b - a)
                      .slice(0, 5)
                      .map(([type, count]) => {
                        const percentage = ((count / summary.total) * 100).toFixed(1);
                        return (
                          <div key={type}>
                            <div className="flex justify-between text-sm mb-1.5">
                              <span className="text-[#1f2329] truncate" title={type}>{type}</span>
                              <span className="text-[#646a73] text-xs">{count} ({percentage}%)</span>
                            </div>
                            <div className="w-full bg-[#f2f3f5] rounded-full h-1.5">
                              <div
                                className="bg-[#f54a45] h-1.5 rounded-full transition-all"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <div className="py-8 flex items-center justify-center">
                    <PageState
                      variant="empty"
                      title={t.common.noData}
                      layout="center"
                      className="border-0 bg-transparent"
                    />
                  </div>
                )}
              </div>

              {/* 按小时分布 */}
              <div className="bg-white rounded-lg border border-[#e5e6eb] p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-4 h-4 text-[#f54a45]" />
                  <h3 className="text-sm font-medium text-[#1f2329]">
                    {t.logs.errors.byHour}
                  </h3>
                </div>
                {summary.byHour && summary.byHour.length > 0 ? (
                  <div className="flex items-end justify-between h-32 gap-1">
                    {summary.byHour.slice(-12).map((item, index) => {
                      const maxCount = Math.max(...summary.byHour.map(h => h.count));
                      const height = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                      return (
                        <div key={index} className="flex-1 flex flex-col items-center group">
                          <div
                            className="w-full bg-[#f54a45] rounded-t transition-all hover:opacity-80"
                            style={{ height: `${height}%`, minHeight: item.count > 0 ? '4px' : '0' }}
                            title={`${item.hour}: ${item.count}`}
                          />
                          <span className="text-xs text-[#8f959e] mt-1.5 truncate w-full text-center">
                            {item.hour.split(':')[0]}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="py-8 flex items-center justify-center">
                    <PageState
                      variant="empty"
                      title={t.common.noData}
                      layout="center"
                      className="border-0 bg-transparent"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 筛选栏 */}
          <div className="bg-white rounded-lg border border-[#e5e6eb] p-4 shadow-sm">
            <div className="flex flex-wrap gap-3">
              {/* 搜索框 */}
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#8f959e]" />
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadLogs()}
                  placeholder={t.logs.errors.searchPlaceholder}
                  className="w-full h-8 pl-9 pr-8 text-sm bg-[#f2f3f5] border-0 rounded-md text-[#1f2329] placeholder:text-[#8f959e] focus:bg-white focus:ring-1 focus:ring-[#3370ff] transition-all"
                />
                {keyword && (
                  <button
                    onClick={() => setKeyword('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-[#e5e6eb] rounded transition-colors"
                  >
                    <X className="w-3 h-3 text-[#8f959e]" />
                  </button>
                )}
              </div>

              {/* 时间范围 */}
              <select
                value={timeRange}
                onChange={(e) => handleTimeRangeChange(e.target.value)}
                className="h-8 px-3 text-sm bg-[#f2f3f5] border-0 rounded-md text-[#1f2329] focus:bg-white focus:ring-1 focus:ring-[#3370ff] transition-all"
              >
                {timeRangeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>

              {/* 错误类型 */}
              <input
                type="text"
                value={filters.errorType || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, errorType: e.target.value || undefined }))}
                placeholder={t.logs.errors.columns.type}
                className="h-8 px-3 text-sm bg-[#f2f3f5] border-0 rounded-md text-[#1f2329] placeholder:text-[#8f959e] focus:bg-white focus:ring-1 focus:ring-[#3370ff] transition-all min-w-[140px]"
              />

              {/* 搜索按钮 */}
              <button
                onClick={loadLogs}
                disabled={isLoading}
                className="h-8 px-4 bg-[#3370ff] text-white text-sm font-medium rounded-md hover:bg-[#2b5fd9] active:bg-[#2555c7] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
              >
                {isLoading ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Search className="w-3.5 h-3.5" />
                )}
                <span>{t.logs.common.search}</span>
              </button>
            </div>
          </div>

          {/* 错误提示 */}
          {error && (
            <div className="bg-[#fff3f2] border border-[#ffccc7] rounded-lg p-3">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-[#f54a45] flex-shrink-0" />
                <span className="text-sm text-[#f54a45]">{error}</span>
              </div>
            </div>
          )}

          {/* 错误日志列表 */}
          <div className="bg-white rounded-lg border border-[#e5e6eb] shadow-sm overflow-hidden">
            {/* 加载中 */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 text-[#8f959e] animate-spin" />
              </div>
            )}

            {/* 错误日志行 */}
            {!isLoading && logs.length > 0 && (
              <div className="divide-y divide-[#e5e6eb]">
                {logs.map((log, index) => {
                  const methodColors = getHttpMethodColor(log.http?.method || 'GET');
                  const isExpanded = expandedRow === `${index}`;
                  
                  return (
                    <div key={index}>
                      <div
                        className={`p-4 hover:bg-[#fef5f4] cursor-pointer transition-colors ${
                          isExpanded ? 'bg-[#fef5f4]' : ''
                        }`}
                        onClick={() => setExpandedRow(isExpanded ? null : `${index}`)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <AlertTriangle className="w-4 h-4 text-[#f54a45] flex-shrink-0" />
                              <span className="text-sm font-medium text-[#f54a45]">
                                {log.error?.type || 'Error'}
                              </span>
                              {log.error?.code && (
                                <span className="px-2 py-0.5 text-xs bg-[#fff3f2] text-[#f54a45] rounded">
                                  {log.error.code}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-[#1f2329] mb-2 line-clamp-2">
                              {log.error?.message || log.message}
                            </p>
                            <div className="flex items-center gap-3 text-xs text-[#646a73]">
                              <span className={`px-2 py-0.5 rounded ${methodColors.bg} ${methodColors.text}`}>
                                {log.http?.method}
                              </span>
                              <span className="truncate max-w-xs" title={log.http?.url}>
                                {log.http?.url}
                              </span>
                              <span>{log.service?.name}</span>
                              <span>{formatDuration(log.http?.duration_ms || 0)}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 ml-4">
                            <span className="text-xs text-[#8f959e]">
                              {new Date(log['@timestamp']).toLocaleString()}
                            </span>
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4 text-[#8f959e]" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-[#8f959e]" />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* 展开详情 */}
                      {isExpanded && (
                        <div className="px-4 py-3 bg-[#fef5f4] border-t border-[#ffccc7]">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                            <div>
                              <span className="text-[#646a73]">Trace ID:</span>
                              <div className="flex items-center gap-1 mt-1">
                                <p className="text-[#1f2329] font-mono text-xs truncate">
                                  {log.trace?.id || '-'}
                                </p>
                                {log.trace?.id && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      copyToClipboard(log.trace?.id || '', `trace-${index}`);
                                    }}
                                    className="p-1 text-[#8f959e] hover:text-[#1f2329] transition-colors"
                                  >
                                    <Copy className={`w-3 h-3 ${copiedId === `trace-${index}` ? 'text-[#00b42a]' : ''}`} />
                                  </button>
                                )}
                              </div>
                            </div>
                            <div>
                              <span className="text-[#646a73]">Request ID:</span>
                              <p className="text-[#1f2329] font-mono text-xs mt-1 truncate">
                                {log.http?.request_id || '-'}
                              </p>
                            </div>
                            <div>
                              <span className="text-[#646a73]">User:</span>
                              <p className="text-[#1f2329] mt-1">{log.user?.name || '-'}</p>
                            </div>
                            <div>
                              <span className="text-[#646a73]">IP:</span>
                              <p className="text-[#1f2329] mt-1">{log.client?.ip || '-'}</p>
                            </div>
                          </div>

                          {/* 错误堆栈 */}
                          {log.error?.stack && (
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-[#1f2329]">
                                  {t.logs.errors.columns.stack}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(log.error?.stack || '', `stack-${index}`);
                                  }}
                                  className="flex items-center gap-1 text-xs text-[#646a73] hover:text-[#1f2329] transition-colors"
                                >
                                  <Copy className={`w-3 h-3 ${copiedId === `stack-${index}` ? 'text-[#00b42a]' : ''}`} />
                                  {t.logs.errors.copyStack}
                                </button>
                              </div>
                              <pre className="p-3 bg-white rounded border border-[#ffccc7] text-xs text-[#f54a45] overflow-x-auto max-h-48 scrollbar-thin">
                                {log.error.stack}
                              </pre>
                            </div>
                          )}

                          {/* 链路追踪链接 */}
                          {log.trace?.id && (
                            <div className="mt-3 pt-3 border-t border-[#ffccc7]">
                              <a
                                href={`/logs/trace?id=${log.trace.id}`}
                                className="inline-flex items-center gap-1 text-sm text-[#3370ff] hover:text-[#2b5fd9] transition-colors"
                              >
                                <ExternalLink className="w-4 h-4" />
                                {t.logs.errors.viewFullTrace}
                              </a>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* 空状态 */}
            {!isLoading && logs.length === 0 && (
              <div className="py-12 flex items-center justify-center">
                <PageState
                  variant="empty"
                  title={t.logs?.errors?.empty || t.common.noData}
                  layout="center"
                  className="border-0 bg-transparent"
                />
              </div>
            )}
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            summary={
              t.common.totalRecords.replace('{total}', total.toLocaleString()) +
              '，' +
              t.common.currentPage
                .replace('{current}', String(page))
                .replace('{total}', String(totalPages))
            }
            prevLabel={t.common.prevPage}
            nextLabel={t.common.nextPage}
            onPageChange={setPage}
          />

        </div>
      </div>
    </div>
  );
}
